"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";

import type { keyPair } from "@repo/models";
import { styles } from "@repo/helpers";

import { InputComponent } from "../input";
import { Label } from "../label";

interface RadioGroupProps {
	id?: string;
	label?: string;
	value: string;
	changeHandler: (value: string) => void;
	values: keyPair[];
	hidden?: boolean;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({ id, label, value, changeHandler, values, hidden }) => {
	return (
		<InputComponent id={id} label={label} hidden={hidden}>
			<RadioGroupInternal id={id} value={value?.toString()} onValueChange={changeHandler}>
				{values.map((option) => (
					<div className="tw-flex tw-items-center tw-space-x-2" key={option.id}>
						<RadioGroupItem value={option.id.toString()} id={option.id.toString()} />
						<Label htmlFor={option.id.toString()}>{option.value}</Label>
					</div>
				))}
			</RadioGroupInternal>
		</InputComponent>
	);
};

const RadioGroupInternal = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
	return <RadioGroupPrimitive.Root className={styles("tw-grid tw-gap-2", className)} {...props} ref={ref} />;
});
RadioGroupInternal.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
	return (
		<RadioGroupPrimitive.Item
			ref={ref}
			className={styles(
				"tw-focus:outline-none tw-focus-visible:ring-1 tw-focus-visible:ring-ring tw-disabled:cursor-not-allowed tw-disabled:opacity-50 tw-aspect-square tw-h-4 tw-w-4 tw-rounded-full tw-border tw-border-primary tw-text-primary tw-shadow",
				className,
			)}
			{...props}
		>
			<RadioGroupPrimitive.Indicator className="tw-flex tw-items-center tw-justify-center">
				<Circle className="tw-h-3.5 tw-w-3.5 tw-fill-primary" />
			</RadioGroupPrimitive.Indicator>
		</RadioGroupPrimitive.Item>
	);
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// export { RadioGroupInternal, RadioGroupItem };
